import { useEffect, useState } from "react";
import { Star, Loader2, MessageSquareReply, Send, Instagram, ExternalLink } from "lucide-react";
import ShareReviewButton from "@/components/ShareReviewButton";
import { useAuth } from "@/lib/auth";
import {
  fetchProductReviews,
  postProductReview,
  adminReplyToReview,
  socialIgPreviewCard,
  socialIgPostReview,
  socialIgPosts,
  dealerIgStatus,
  dealerIgPreviewCard,
  dealerIgPostReview,
  dealerIgPosts,
  isAdminUser,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

// Collapse a review to its first paragraph. Falls back to a character limit
// when the review is a single long paragraph with no blank-line breaks.
function splitFirstParagraph(text) {
  const t = (text || "").trim();
  if (!t) return { summary: "", hasMore: false };
  const paras = t.split(/\n\s*\n/).filter((p) => p.trim());
  if (paras.length > 1) return { summary: paras[0].trim(), hasMore: true };
  if (t.length > 220) return { summary: t.slice(0, 220).trim() + "…", hasMore: true };
  return { summary: t, hasMore: false };
}

function Stars({ value, size = 14, onClick }) {
  const filled = Math.round(value || 0);
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={onClick ? () => onClick(i) : undefined}
          disabled={!onClick}
          className={`${
            onClick ? "cursor-pointer hover:scale-110 transition" : "cursor-default"
          }`}
          aria-label={`${i} stars`}
        >
          <Star
            size={size}
            className={
              i <= filled ? "fill-amber-400 text-amber-400" : "text-zinc-300"
            }
            strokeWidth={1.7}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Product reviews section — full-width, lives directly under the hero on
 * `/d/<slug>/product/<slug>`.  Source of truth: Airtable `Comments` table.
 */
export default function ProductReviews({ productRecordId }) {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const recId = user?.user_record_id || user?.record_id || "";
  const [dealerIg, setDealerIg] = useState(null); // dealer's own connected Instagram { connected, username }
  const canPost = isAdmin || !!dealerIg?.connected;
  const igTarget = isAdmin ? "@orevital" : `@${dealerIg?.username || "my-instagram"}`;
  const [igPosts, setIgPosts] = useState({}); // comment_record_id -> permalink
  const [igPreview, setIgPreview] = useState(null); // { id, dataUrl, style, fmt }
  const [igRendering, setIgRendering] = useState(false);
  const [igPosting, setIgPosting] = useState(false);
  const [justPosted, setJustPosted] = useState(null); // comment record id of the review the user just wrote
  useEffect(() => {
    if (!user || !recId) return;
    if (isAdmin) {
      socialIgPosts(recId)
        .then((r) => setIgPosts(Object.fromEntries((r.posts || []).map((p) => [p.comment_record_id, p.permalink || "posted"]))))
        .catch(() => {});
      return;
    }
    dealerIgStatus(recId).then((s) => {
      setDealerIg(s);
      if (s?.connected) {
        dealerIgPosts(recId)
          .then((r) => setIgPosts(Object.fromEntries((r.posts || []).map((p) => [p.comment_record_id, p.permalink || "posted"]))))
          .catch(() => {});
      }
    }).catch(() => setDealerIg(null));
  }, [isAdmin, user, recId]);
  async function igPreviewFor(id, style = "classic", fmt = "square") {
    setIgRendering(true);
    try {
      const r = isAdmin ? await socialIgPreviewCard(recId, id, style, fmt) : await dealerIgPreviewCard(recId, id, style, fmt);
      setIgPreview({ id, dataUrl: r.data_url, style, fmt });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render the review card");
    } finally {
      setIgRendering(false);
    }
  }
  async function igPostNow() {
    if (!igPreview) return;
    setIgPosting(true);
    try {
      const m = window.location.pathname.match(/^\/d\/([^/?#]+)/);
      const r = isAdmin
        ? await socialIgPostReview(recId, igPreview.id, m ? m[1] : "", igPreview.style, igPreview.fmt)
        : await dealerIgPostReview(recId, igPreview.id, igPreview.style, igPreview.fmt);
      setIgPosts((p) => ({ ...p, [igPreview.id]: r.permalink || "posted" }));
      setIgPreview(null);
      const what = igPreview.fmt === "story" ? "Story" : "post";
      toast.success(r.already ? `This review is already on Instagram as a ${what}` : `Posted to ${igTarget} as a ${what}`, { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Instagram post failed", { duration: 9000 });
    } finally {
      setIgPosting(false);
    }
  }
  const [data, setData] = useState({
    reviews: [],
    rating_avg: 0,
    rating_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [replyOpen, setReplyOpen] = useState({}); // commentId → bool
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState({});
  const [expanded, setExpanded] = useState({}); // commentId → bool (read more)
  // Show only one row by default (3 cards on lg, 2 on sm, 1 on mobile).
  // Tap "Show all" to expand the full list.
  const [showAll, setShowAll] = useState(false);

  async function load() {
    if (!productRecordId) return;
    setLoading(true);
    try {
      const d = await fetchProductReviews(productRecordId);
      setData(d);
    } catch {
      // swallow — show empty state
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Deep link from the review-request email: /product/<id>#reviews opens the form.
    if (typeof window !== "undefined" && window.location.hash === "#reviews") {
      setShowForm(true);
      setTimeout(() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRecordId]);

  async function submitReview() {
    if (!user) {
      toast.error("Sign in to leave a review");
      return;
    }
    if (!content.trim()) {
      toast.error("Please write a review");
      return;
    }
    setSubmitting(true);
    try {
      const res = await postProductReview({
        productRecordId,
        callerUserRecordId: user.user_record_id || user.record_id,
        rating,
        subject: subject.trim(),
        content: content.trim(),
      });
      if (res?.instagram?.permalink) {
        toast.success("Your review was shared on OreVital's Instagram!", { duration: 7000 });
      }
      if (res?.points_awarded > 0) {
        toast.success(`$${res.points_awarded} in rewards added (balance ${res.points_display || ""})`, { duration: 8000 });
      } else if (res?.points_note) {
        toast.info(res.points_note, { duration: 8000 });
      } else {
        toast.success("Thanks for your review!");
      }
      setSubject("");
      setContent("");
      setRating(5);
      setShowForm(false);
      if (res?.review?.id && (res.review.content || "").length >= 10) setJustPosted(res.review.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save review");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReply(commentRecordId) {
    const text = (replyText[commentRecordId] || "").trim();
    if (!text) return;
    setReplying((s) => ({ ...s, [commentRecordId]: true }));
    try {
      await adminReplyToReview({
        commentRecordId,
        callerUserRecordId: user.user_record_id || user.record_id,
        content: text,
      });
      toast.success("Reply posted");
      setReplyText((s) => ({ ...s, [commentRecordId]: "" }));
      setReplyOpen((s) => ({ ...s, [commentRecordId]: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reply");
    } finally {
      setReplying((s) => ({ ...s, [commentRecordId]: false }));
    }
  }

  return (
    <section
      id="reviews"
      className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5"
      data-testid="product-reviews"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-zinc-100">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">
            Real Reviews From Real Customers
          </h2>
          <div className="flex items-center gap-2 mt-0.5">
            <Stars value={data.rating_avg} size={15} />
            <span className="text-xs text-zinc-500">
              {data.rating_avg || 0} · {data.rating_count} review
              {data.rating_count === 1 ? "" : "s"}
            </span>
          </div>
          {data.bundle_items_included && (
            <div className="text-[11px] text-zinc-500 mt-0.5" data-testid="bundle-reviews-note">
              Includes reviews of the products inside this bundle
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={() => setShowForm((v) => !v)}
            className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
            data-testid="review-write-btn"
            size="sm"
          >
            {showForm ? "Cancel" : "Write a review"}
          </Button>
          {!showForm && data.review_points > 0 && (
            <span className="text-[10px] font-semibold text-amber-700" data-testid="review-points-hint">
              Earn ${data.review_points} in rewards when you share your review on your Instagram or Facebook
            </span>
          )}
        </div>
      </div>

      {showForm && (
        <div className="rounded-2xl bg-violet-50/40 border border-violet-200 p-4 mb-4 space-y-3">
          <div>
            <Label className="text-xs">Your rating</Label>
            <div className="mt-1">
              <Stars value={rating} size={22} onClick={setRating} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A few words sum it up"
              className="h-10 mt-1"
              data-testid="review-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Your review</Label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              placeholder="Tell us what you loved about it…"
              className="mt-1 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="review-content"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={submitReview}
              disabled={submitting}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="review-submit"
            >
              {submitting ? (
                <Loader2 className="animate-spin mr-1" size={13} />
              ) : (
                <Send size={13} className="mr-1" />
              )}
              Post review
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-400" size={20} />
        </div>
      ) : data.reviews.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-500">
          No reviews yet — be the first!
        </div>
      ) : (
        <>
          {justPosted && (
            <div className="mb-4 rounded-2xl border border-pink-200 bg-pink-50/60 p-4 flex flex-wrap items-center gap-3" data-testid="share-your-review-banner">
              <Instagram size={18} className="text-pink-600 shrink-0" />
              <div className="flex-1 min-w-[200px] text-sm text-zinc-800">
                <div className="font-semibold">Thanks! Share your review on your Instagram or Facebook{data?.review_points > 0 ? ` and earn $${data.review_points}` : ""}</div>
                <div className="text-xs text-zinc-600">We made you a branded review card — tap share to open Instagram/Facebook with it. Your reward is added once you've shared.</div>
              </div>
              <ShareReviewButton commentRecordId={justPosted} label="Share my review" testId="share-my-review"
                callerUserRecordId={user?.user_record_id || user?.record_id || ""} rewardDollars={data?.review_points || 0} />
              <button type="button" onClick={() => setJustPosted(null)} className="text-xs text-zinc-400 hover:text-zinc-600" data-testid="share-banner-dismiss">Dismiss</button>
            </div>
          )}
          {igPreview && (
            <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => !igPosting && setIgPreview(null)} data-testid="ig-preview-modal">
              <div className="bg-white rounded-2xl p-4 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm font-semibold text-zinc-900">Preview — Instagram review card</div>
                <div className="flex flex-wrap gap-2 text-xs" data-testid="ig-card-options">
                  <div className="inline-flex rounded-full border border-zinc-200 p-0.5" data-testid="ig-style-picker">
                    {[["classic", "Navy & gold"], ["light", "Light · photo first"]].map(([v, l]) => (
                      <button key={v} type="button" disabled={igRendering || igPosting} onClick={() => igPreviewFor(igPreview.id, v, igPreview.fmt)}
                        className={`px-3 py-1 rounded-full font-medium transition-colors ${igPreview.style === v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                        data-testid={`ig-style-${v}`}>{l}</button>
                    ))}
                  </div>
                  <div className="inline-flex rounded-full border border-zinc-200 p-0.5" data-testid="ig-format-picker">
                    {[["square", "Post 1:1"], ["story", "Story 9:16"]].map(([v, l]) => (
                      <button key={v} type="button" disabled={igRendering || igPosting} onClick={() => igPreviewFor(igPreview.id, igPreview.style, v)}
                        className={`px-3 py-1 rounded-full font-medium transition-colors ${igPreview.fmt === v ? "bg-pink-600 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                        data-testid={`ig-format-${v}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="relative flex justify-center bg-zinc-50 rounded-xl border border-zinc-200 p-2">
                  <img src={igPreview.dataUrl} alt="Review card" className={`rounded-lg object-contain ${igPreview.fmt === "story" ? "max-h-[60vh]" : "w-full"} ${igRendering ? "opacity-40" : ""}`} data-testid="ig-preview-img" />
                  {igRendering && <Loader2 className="animate-spin absolute inset-0 m-auto text-zinc-500" size={22} data-testid="ig-preview-rendering" />}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" className="rounded-full" disabled={igPosting} onClick={() => setIgPreview(null)} data-testid="ig-preview-cancel">Cancel</Button>
                  <Button className="rounded-full bg-pink-600 hover:bg-pink-700 text-white" disabled={igPosting || igRendering} onClick={igPostNow} data-testid="ig-preview-post">
                    {igPosting ? <Loader2 className="animate-spin" size={14} /> : <><Instagram size={14} className="mr-1" /> {igPreview.fmt === "story" ? `Post Story to ${igTarget}` : `Post to ${igTarget}`}</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(showAll ? data.reviews : data.reviews.slice(0, 3)).map((r) => {
              const open = !!replyOpen[r.id];
              const { summary, hasMore } = splitFirstParagraph(r.content);
              const isOpen = !!expanded[r.id];
              const shownContent = isOpen || !hasMore ? r.content : summary;
              return (
              <li
                key={r.id}
                className="rounded-2xl bg-zinc-50 border border-zinc-100 p-4 flex flex-col"
                data-testid={`review-${r.comment_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <Stars value={r.rating} size={13} />
                  <span className="text-[11px] text-zinc-400">
                    {fmtDate(r.created_at)}
                  </span>
                </div>
                {r.product_name && (
                  <span className="mt-2 self-start inline-block rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-semibold px-2 py-0.5" data-testid={`review-product-${r.comment_id}`}>
                    {r.product_name}
                  </span>
                )}
                {r.subject && (
                  <h3 className="mt-2 text-sm font-bold text-zinc-900 leading-snug">
                    {r.subject}
                  </h3>
                )}
                <p className="mt-1 text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap">
                  {shownContent}
                </p>
                {hasMore && (
                  <button
                    onClick={() =>
                      setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))
                    }
                    className="mt-1 self-start text-[11px] font-semibold text-violet-700 hover:underline"
                    data-testid={`review-toggle-${r.comment_id}`}
                  >
                    {isOpen ? "Read less" : "Read more"}
                  </button>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold text-zinc-500">— {r.author}</div>
                  {(r.content || "").length >= 10 && (
                    <ShareReviewButton commentRecordId={r.id} label="Share" testId={`share-review-${r.comment_id}`}
                      callerUserRecordId={user?.user_record_id || user?.record_id || ""} rewardDollars={data?.review_points || 0} />
                  )}
                </div>

                {/* Admin reply thread */}
                {r.replies?.length > 0 && (
                  <ul className="mt-3 pt-3 border-t border-zinc-200 space-y-2">
                    {r.replies.map((rp) => (
                      <li
                        key={rp.id}
                        className="rounded-lg bg-emerald-50/60 border border-emerald-200 p-2 text-[11px] text-zinc-800 leading-snug"
                        data-testid={`review-reply-${rp.comment_id}`}
                      >
                        <div className="font-bold text-emerald-700 flex items-center gap-1">
                          <MessageSquareReply size={11} /> Reply from {rp.author}
                          <span className="ml-auto text-emerald-600/60 text-[10px] font-normal">
                            {fmtDate(rp.created_at)}
                          </span>
                        </div>
                        <div className="mt-1 whitespace-pre-wrap">
                          {rp.content}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {canPost && (
                  <div className="pt-2 flex items-center gap-2" data-testid={`ig-share-row-${r.comment_id}`}>
                    {igPosts[r.id] ? (
                      <a href={igPosts[r.id].startsWith("http") ? igPosts[r.id] : "#"} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-pink-700 bg-pink-50 rounded-full px-2 py-1"
                        data-testid={`ig-posted-${r.comment_id}`}>
                        <Instagram size={11} /> On Instagram <ExternalLink size={10} />
                      </a>
                    ) : (
                      <button type="button" onClick={() => igPreviewFor(r.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-pink-700 border border-pink-200 hover:bg-pink-50 rounded-full px-2 py-1"
                        data-testid={`ig-share-btn-${r.comment_id}`}>
                        <Instagram size={11} /> {isAdmin ? "Post to Instagram" : `Post to ${igTarget}`}
                      </button>
                    )}
                    {igPosts[r.id] && (
                      <button type="button" onClick={() => igPreviewFor(r.id, "classic", "story")}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 border border-zinc-200 hover:bg-zinc-50 rounded-full px-2 py-1"
                        data-testid={`ig-story-btn-${r.comment_id}`}>
                        <Instagram size={11} /> Story
                      </button>
                    )}
                  </div>
                )}
                {/* Admin reply form */}
                {isAdmin && (
                  <div className="mt-auto pt-3">
                    {open ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={replyText[r.id] || ""}
                          onChange={(e) =>
                            setReplyText((s) => ({ ...s, [r.id]: e.target.value }))
                          }
                          rows={2}
                          placeholder="Reply as OreVital…"
                          className="w-full rounded-lg border border-zinc-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          data-testid={`reply-text-${r.comment_id}`}
                        />
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() =>
                              setReplyOpen((s) => ({ ...s, [r.id]: false }))
                            }
                            className="text-[10px] px-2 py-1 rounded text-zinc-500 hover:bg-zinc-100"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => submitReply(r.id)}
                            disabled={replying[r.id]}
                            className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1"
                            data-testid={`reply-submit-${r.comment_id}`}
                          >
                            {replying[r.id] ? (
                              <Loader2 className="animate-spin" size={10} />
                            ) : (
                              <Send size={10} />
                            )}
                            Post
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() =>
                          setReplyOpen((s) => ({ ...s, [r.id]: true }))
                        }
                        className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1 font-semibold"
                        data-testid={`reply-open-${r.comment_id}`}
                      >
                        <MessageSquareReply size={11} /> Admin reply
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {data.reviews.length > 3 && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAll((v) => !v)}
              className="rounded-full text-xs font-semibold"
              data-testid="reviews-toggle"
            >
              {showAll
                ? "Show less"
                : `Show all ${data.reviews.length} reviews`}
            </Button>
          </div>
        )}
        </>
      )}
    </section>
  );
}
